import { useCallback, useMemo, useRef, useState } from "react";
import { useGomakeAxios, useGomakeRouter, useSnackBar } from "@/hooks";
import {
  getDefaultCustomerSkusByBoardMissionIdApi,
  getDefaultCustomerSkusByCustomerIdApi,
} from "@/services/api-service/customer-skus/customer-skus-endpoints";
import { ICreateItemsFromWorkTypesRequest } from "@/services/api-service/customer-skus/customer-skus.types";
import {
  addDocumentContactApi,
  changeDocumentClientApi,
  createItemsFromWorkTypesApi,
  createNewDocumentApi,
  getDocumentApi,
  getIfCartExistApi,
  updateDocumentApi,
} from "@/services/api-service/generic-doc/documents-api";
import { getClientContactsApi } from "@/services/api-service/customers/customers-api";
import { DOCUMENT_TYPE } from "@/pages-components/quotes/enums";
import {
  IReorderSkuGroup,
  IReorderSkuItem,
  ReorderSkuStep,
  dedupeReorderSkuItems,
  isSameReorderSku,
  mapDefaultCustomerSkuGroups,
  mapToReorderSkuItem,
} from "./types";

export interface IReorderSkuOpenContext {
  documentId?: string;
  clientId?: string;
  documentHasClient?: boolean;
  onSuccess?: () => void;
  skipConfirmRelated?: boolean;
  selectAllRelated?: boolean;
}

const unwrapToArray = (raw: unknown): unknown[] => {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (typeof raw === "string") {
    return [raw];
  }
  if (!raw || typeof raw !== "object") {
    return [];
  }

  const record = raw as Record<string, unknown>;
  if (Array.isArray(record.data)) {
    return record.data;
  }

  const nestedData = (record.data as { data?: unknown } | undefined)?.data;
  if (Array.isArray(nestedData)) {
    return nestedData;
  }

  const indexKeys = Object.keys(record)
    .filter((key) => /^\d+$/.test(key))
    .sort((a, b) => Number(a) - Number(b));
  if (indexKeys.length) {
    return indexKeys.map((key) => record[key]);
  }

  return [];
};

const normalizeRelatedList = (raw: unknown): IReorderSkuItem[] =>
  unwrapToArray(raw)
    .map((item) => mapToReorderSkuItem(item))
    .filter(Boolean) as IReorderSkuItem[];

const toPrice = (value: unknown) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const extractDocumentId = (res: any): string | null => {
  const data = res?.data;
  const id =
    data?.result?.id ||
    data?.documentId ||
    data?.id ||
    data?.data?.documentId ||
    data?.data?.id ||
    res?.result?.id ||
    res?.documentId ||
    res?.id ||
    null;
  return id ? String(id) : null;
};

const extractDocumentClientId = (source: unknown): string | null => {
  if (!source || typeof source !== "object") {
    return null;
  }

  const record = source as Record<string, unknown>;
  const data =
    record.data && typeof record.data === "object"
      ? (record.data as Record<string, unknown>)
      : record;
  const result =
    data.result && typeof data.result === "object"
      ? (data.result as Record<string, unknown>)
      : data;
  const client =
    result.client && typeof result.client === "object"
      ? (result.client as Record<string, unknown>)
      : data.client && typeof data.client === "object"
        ? (data.client as Record<string, unknown>)
        : null;
  const id =
    client?.id ||
    client?.clientId ||
    result.customerID ||
    result.customerId ||
    result.clientId ||
    data.customerID ||
    data.customerId ||
    data.clientId ||
    record.customerID ||
    record.clientId ||
    null;

  return id ? String(id) : null;
};

const mapCustomerContact = (raw: unknown) => {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const row = raw as Record<string, unknown>;
  const id = String(row.id || row.ID || row.contactId || "");
  const name = String(
    row.name || row.Name || row.contactName || row.ContactName || ""
  ).trim();
  if (!id && !name) {
    return null;
  }
  return {
    id,
    name,
    mail: String(
      row.mail || row.Mail || row.email || row.contactMail || ""
    ),
    phone: String(
      row.phone || row.Phone || row.contactPhone || ""
    ),
    isMain: !!(
      row.isMainContact ||
      row.IsMainContact ||
      row.isDefault ||
      row.isMain
    ),
  };
};

const useReorderSkuModal = () => {
  const { callApi } = useGomakeAxios();
  const { navigate } = useGomakeRouter();
  const {
    alertFaultGetData,
    alertFaultAdded,
    alertFaultUpdate,
    alertFault,
    alertSuccessAdded,
  } = useSnackBar();

  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<ReorderSkuStep>("selectRelated");
  const [parentSku, setParentSku] = useState<IReorderSkuItem | null>(null);
  const [relatedSkus, setRelatedSkus] = useState<IReorderSkuItem[]>([]);
  const [skuGroups, setSkuGroups] = useState<IReorderSkuGroup[]>([]);
  const [selectedRelatedIds, setSelectedRelatedIds] = useState<string[]>([]);
  const [isLoadingRelated, setIsLoadingRelated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] = useState(false);
  const [pendingQuoteId, setPendingQuoteId] = useState<string | null>(null);
  const [reorderContext, setReorderContext] = useState<IReorderSkuOpenContext>(
    {}
  );
  const pendingWorkTypesRef = useRef<{
    payloads: ICreateItemsFromWorkTypesRequest[];
    clientId?: string;
  } | null>(null);

  const resetState = useCallback(() => {
    setStep("selectRelated");
    setParentSku(null);
    setRelatedSkus([]);
    setSkuGroups([]);
    setSelectedRelatedIds([]);
    setIsLoadingRelated(false);
    setIsSubmitting(false);
    setReorderContext({});
  }, []);

  const closeModal = useCallback(() => {
    if (isSubmitting || isLoadingRelated) {
      return;
    }
    setIsOpen(false);
    resetState();
  }, [isLoadingRelated, isSubmitting, resetState]);

  const applyRelatedList = useCallback(
    (parent: IReorderSkuItem, items: IReorderSkuItem[], selectAll: boolean) => {
      setSkuGroups([]);
      const parentGroupName = parent.skuGroupName?.trim().toLowerCase();
      const parentSkuName = parent.sku?.trim().toLowerCase();
      const parentIsGroup =
        !!parent.isSkuGroup ||
        (!!parentGroupName && parentSkuName === parentGroupName);
      const related = dedupeReorderSkuItems(items)
        .filter((item) => {
          if (!parentGroupName) {
            return true;
          }
          return item.sku.trim().toLowerCase() !== parentGroupName;
        })
        .map((item) => ({
          ...item,
          boardMissionId: item.boardMissionId || parent.boardMissionId,
          orderItemId: item.orderItemId || parent.orderItemId,
          unitPrice: item.unitPrice ?? parent.unitPrice,
          quantity: item.quantity > 0 ? item.quantity : parent.quantity || 1,
          skuGroupName: item.skuGroupName || parent.skuGroupName,
          isParent: false,
          isSkuGroup: false,
        }));
      const hasParentInList = related.some((item) =>
        isSameReorderSku(item, parent)
      );
      const relatedWithParent = parentIsGroup
        ? related
        : related.length
          ? hasParentInList
            ? related.map((item) => ({
                ...item,
                isParent: isSameReorderSku(item, parent),
              }))
            : [{ ...parent, isParent: true }, ...related]
          : [{ ...parent, isParent: true }];
      const listWithoutGroupName = parentGroupName
        ? relatedWithParent.filter(
            (item) => item.sku.trim().toLowerCase() !== parentGroupName
          )
        : relatedWithParent;
      const preselectedIds = selectAll
        ? listWithoutGroupName.map((item) => item.id)
        : relatedWithParent
            .filter((item) => isSameReorderSku(item, parent))
            .map((item) => item.id);

      setRelatedSkus(listWithoutGroupName);
      setSelectedRelatedIds(
        preselectedIds.length
          ? preselectedIds
          : listWithoutGroupName[0]
            ? [listWithoutGroupName[0].id]
            : []
      );
      setStep("selectRelated");
    },
    []
  );

  const applyGroupedList = useCallback(
    (parent: IReorderSkuItem, groups: IReorderSkuGroup[], selectAll: boolean) => {
      const flat = groups.flatMap((group) => group.skus);
      const parentGroupName = parent.skuGroupName?.trim().toLowerCase();
      const parentSkuName = parent.sku?.trim().toLowerCase();
      const parentIsGroup =
        !!parent.isSkuGroup ||
        (!!parentGroupName && parentSkuName === parentGroupName);
      const matchingGroups = parentGroupName
        ? groups.filter(
            (group) => group.skuGroupName.trim().toLowerCase() === parentGroupName
          )
        : [];

      let preselectedIds: string[] = [];
      if (selectAll || parentIsGroup) {
        const targetGroups = matchingGroups.length ? matchingGroups : groups;
        preselectedIds = targetGroups.flatMap((group) =>
          group.skus.map((item) => item.id)
        );
      } else {
        const matches = flat.filter((item) => {
          if (item.sku.trim().toLowerCase() !== parentSkuName) {
            return false;
          }
          if (
            parent.orderItemId &&
            item.orderItemId &&
            item.orderItemId !== parent.orderItemId
          ) {
            return false;
          }
          if (
            parentGroupName &&
            item.skuGroupName?.trim().toLowerCase() !== parentGroupName
          ) {
            return false;
          }
          return true;
        });
        preselectedIds = matches.length
          ? [matches[0].id]
          : flat[0]
            ? [flat[0].id]
            : [];
      }

      setSkuGroups(groups);
      setRelatedSkus(flat);
      setSelectedRelatedIds(preselectedIds);
      setStep("selectRelated");
    },
    []
  );

  const loadRelatedSkus = useCallback(
    async (parent: IReorderSkuItem, selectAll: boolean) => {
      const customerId = parent.customerId?.trim();
      if (customerId) {
        setIsLoadingRelated(true);
        const callBack = (res: { success?: boolean; data?: unknown }) => {
          setIsLoadingRelated(false);
          const groups = res?.success
            ? mapDefaultCustomerSkuGroups(unwrapToArray(res.data))
            : [];
          if (!res?.success || !groups.length) {
            if (!res?.success) {
              alertFaultGetData();
            }
            applyRelatedList(parent, [parent], true);
            return;
          }
          applyGroupedList(parent, groups, selectAll);
        };

        await getDefaultCustomerSkusByCustomerIdApi(callApi, callBack, customerId);
        return;
      }

      if (!parent.boardMissionId) {
        applyRelatedList(parent, [parent], true);
        return;
      }

      setIsLoadingRelated(true);
      const callBack = (res: { success?: boolean; data?: unknown }) => {
        setIsLoadingRelated(false);
        if (!res?.success) {
          alertFaultGetData();
          applyRelatedList(parent, [parent], true);
          return;
        }

        applyRelatedList(parent, normalizeRelatedList(res.data), selectAll);
      };

      await getDefaultCustomerSkusByBoardMissionIdApi(
        callApi,
        callBack,
        parent.boardMissionId
      );
    },
    [alertFaultGetData, applyGroupedList, applyRelatedList, callApi]
  );

  const openReorderSkuModal = useCallback(
    (rawParent: unknown, context?: IReorderSkuOpenContext) => {
      const mapped = mapToReorderSkuItem(rawParent, true);
      if (!mapped) {
        alertFaultGetData();
        return;
      }
      const customerId = mapped.customerId || context?.clientId || "";
      const parent = customerId ? { ...mapped, customerId } : mapped;
      const skipConfirmRelated = !!context?.skipConfirmRelated;
      const selectAllRelated = !!context?.selectAllRelated;
      setParentSku(parent);
      setRelatedSkus([]);
      setSkuGroups([]);
      setSelectedRelatedIds([]);
      setReorderContext({
        documentId: context?.documentId || "",
        clientId: context?.clientId || "",
        documentHasClient: context?.documentHasClient,
        onSuccess: context?.onSuccess,
        skipConfirmRelated,
        selectAllRelated,
      });
      setIsOpen(true);
      setStep("selectRelated");
      void loadRelatedSkus(parent, selectAllRelated);
    },
    [alertFaultGetData, loadRelatedSkus]
  );

  const selectedSkus = useMemo(
    () => relatedSkus.filter((item) => selectedRelatedIds.includes(item.id)),
    [relatedSkus, selectedRelatedIds]
  );

  const onToggleRelated = useCallback((skuId: string) => {
    setSelectedRelatedIds((prev) =>
      prev.includes(skuId)
        ? prev.filter((id) => id !== skuId)
        : [...prev, skuId]
    );
  }, []);

  const onToggleGroup = useCallback(
    (groupId: string) => {
      const group = skuGroups.find((item) => item.id === groupId);
      if (!group) {
        return;
      }
      const ids = group.skus.map((item) => item.id);
      setSelectedRelatedIds((prev) => {
        const allSelected = ids.length > 0 && ids.every((id) => prev.includes(id));
        if (allSelected) {
          return prev.filter((id) => !ids.includes(id));
        }
        return Array.from(new Set([...prev, ...ids]));
      });
    },
    [skuGroups]
  );

  const onToggleAllGroups = useCallback(() => {
    const ids = relatedSkus.map((item) => item.id);
    setSelectedRelatedIds((prev) => {
      const allSelected = ids.length > 0 && ids.every((id) => prev.includes(id));
      return allSelected ? [] : ids;
    });
  }, [relatedSkus]);

  const onChangeQuantity = useCallback((skuId: string, quantity: number) => {
    setRelatedSkus((prev) =>
      prev.map((item) =>
        item.id === skuId ? { ...item, quantity } : item
      )
    );
  }, []);

  const hasInvalidQuantity = useMemo(
    () =>
      !selectedSkus.length ||
      selectedSkus.some(
        (item) =>
          !item.quantity || item.quantity <= 0 || !Number.isFinite(item.quantity)
      ),
    [selectedSkus]
  );

  const buildWorkTypesPayloads = useCallback(
    (recalculatePrice: boolean): ICreateItemsFromWorkTypesRequest[] => {
      if (!selectedSkus.length) {
        return [];
      }

      const clientFields = reorderContext.clientId
        ? { clientId: reorderContext.clientId }
        : {};

      if (skuGroups.length) {
        return skuGroups.flatMap((group) => {
          const groupIds = new Set(group.skus.map((item) => item.id));
          const items = relatedSkus.filter(
            (item) => groupIds.has(item.id) && selectedRelatedIds.includes(item.id)
          );
          if (!items.length || !group.orderItemId) {
            return [];
          }
          return [
            {
              orderItemId: group.orderItemId,
              ...clientFields,
              price: recalculatePrice ? 0 : toPrice(group.price),
              recalculatePrice,
              workTypes: items.map((item) => ({
                name: item.sku,
                quantity: item.quantity,
              })),
              skuGroupName: group.skuGroupName || "",
            },
          ];
        });
      }

      if (!parentSku?.orderItemId) {
        return [];
      }

      return [
        {
          orderItemId: parentSku.orderItemId,
          ...clientFields,
          price: recalculatePrice ? 0 : toPrice(parentSku.unitPrice),
          recalculatePrice,
          workTypes: selectedSkus.map((item) => ({
            name: item.sku,
            quantity: item.quantity,
          })),
          skuGroupName: parentSku.skuGroupName || "",
        },
      ];
    },
    [parentSku, relatedSkus, reorderContext.clientId, selectedRelatedIds, selectedSkus, skuGroups]
  );

  const assignClientToDocument = useCallback(
    async (documentId?: string, clientId?: string) => {
      if (!documentId || !clientId) {
        return;
      }

      await changeDocumentClientApi(callApi, () => undefined, {
        documentType: DOCUMENT_TYPE.quote,
        client: {
          documentID: documentId,
          clientId,
        },
      });
    },
    [callApi]
  );

  const assignCustomerContactsToDocument = useCallback(
    async (documentId?: string, clientId?: string) => {
      if (!documentId || !clientId) {
        return;
      }

      let contacts: NonNullable<ReturnType<typeof mapCustomerContact>>[] = [];
      await getClientContactsApi(
        callApi,
        (res) => {
          if (!res?.success) {
            return;
          }
          contacts = unwrapToArray(res.data)
            .map(mapCustomerContact)
            .filter(Boolean) as NonNullable<ReturnType<typeof mapCustomerContact>>[];
        },
        { ClientId: clientId }
      );

      if (!contacts.length) {
        return;
      }

      const contact =
        contacts.find((item) => item.isMain) || contacts[0];

      await addDocumentContactApi(callApi, () => undefined, {
        documentType: DOCUMENT_TYPE.quote,
        contact: {
          contactID: contact.id,
          contactName: contact.name,
          contactMail: contact.mail,
          contactPhone: contact.phone,
          documentID: documentId,
        },
      });
    },
    [callApi]
  );

  const postWorkTypesAndOpenQuote = useCallback(
    async (
      payloads: ICreateItemsFromWorkTypesRequest[],
      documentId?: string,
      clientId?: string,
      shouldAddContacts = false,
      hasClient = false
    ) => {
      const resolvedClientId =
        clientId || payloads[0]?.clientId || reorderContext.clientId;
      if (!hasClient) {
        await assignClientToDocument(documentId, resolvedClientId);
      }
      if (shouldAddContacts && !hasClient) {
        await assignCustomerContactsToDocument(documentId, resolvedClientId);
      }

      const res = await createItemsFromWorkTypesApi(
        callApi,
        () => undefined,
        payloads.map((payload) => ({
          ...payload,
          ...(documentId ? { documentId } : {}),
          ...(resolvedClientId ? { clientId: resolvedClientId } : {}),
          documentType: DOCUMENT_TYPE.quote,
        }))
      );
      if (!res?.success) {
        setIsSubmitting(false);
        const message = (res as { message?: string } | undefined)?.message;
        alertFault(message || "customerSkus.reorderFailed");
        return;
      }

      const onSuccess = reorderContext.onSuccess;
      pendingWorkTypesRef.current = null;
      setIsSubmitting(false);
      setIsOpen(false);
      resetState();
      alertSuccessAdded();
      if (onSuccess) {
        onSuccess();
      } else {
        navigate("/quote");
      }
    },
    [
      alertFault,
      alertSuccessAdded,
      assignClientToDocument,
      assignCustomerContactsToDocument,
      callApi,
      navigate,
      reorderContext.clientId,
      reorderContext.onSuccess,
      resetState,
    ]
  );

  const resolveQuoteDocumentId = useCallback(async () => {
    if (reorderContext.documentId) {
      if (typeof reorderContext.documentHasClient === "boolean") {
        return {
          documentId: reorderContext.documentId,
          isNewDocument: false,
          hasClient: reorderContext.documentHasClient,
        };
      }

      let hasClient = false;
      await getDocumentApi(
        callApi,
        (res) => {
          if (!res?.success) {
            return;
          }
          hasClient = !!extractDocumentClientId(res);
        },
        {
          documentType: DOCUMENT_TYPE.quote,
          Id: reorderContext.documentId,
          id: reorderContext.documentId,
        },
        false
      );

      return {
        documentId: reorderContext.documentId,
        isNewDocument: false,
        hasClient,
      };
    }

    let existingId: string | null = null;
    let existingHasClient = false;
    await getIfCartExistApi(
      callApi,
      (res) => {
        if (res?.data?.succ || res?.success) {
          existingId =
            extractDocumentId(res) ||
            (res?.data?.result?.id ? String(res.data.result.id) : null);
          existingHasClient = !!extractDocumentClientId(res);
        }
      },
      { documentType: DOCUMENT_TYPE.quote }
    );

    if (existingId) {
      return {
        documentId: existingId,
        isNewDocument: false,
        hasClient: existingHasClient,
      };
    }

    let createdId: string | null = null;
    let needsStatusChange = false;
    await createNewDocumentApi(
      callApi,
      (res) => {
        if (!res?.success) {
          return;
        }
        createdId = extractDocumentId(res);
        needsStatusChange = !!res?.data?.isAnotherQuoteInCreate;
      },
      {
        documentType: DOCUMENT_TYPE.quote,
        ...(reorderContext.clientId && { clientId: reorderContext.clientId }),
      }
    );

    return {
      documentId: createdId || undefined,
      needsStatusChange,
      isNewDocument: true,
      hasClient: false,
    };
  }, [
    callApi,
    reorderContext.clientId,
    reorderContext.documentHasClient,
    reorderContext.documentId,
  ]);

  const submitReorder = useCallback(
    async (recalculatePrice: boolean) => {
      if (!parentSku || hasInvalidQuantity) {
        alertFaultAdded();
        return;
      }

      const payloads = buildWorkTypesPayloads(recalculatePrice);
      if (!payloads.length || payloads.some((payload) => !payload.workTypes.length)) {
        alertFaultAdded();
        return;
      }

      setIsSubmitting(true);
      try {
        const resolved = await resolveQuoteDocumentId();

        if (resolved.needsStatusChange && resolved.documentId) {
          pendingWorkTypesRef.current = {
            payloads,
            clientId: reorderContext.clientId,
          };
          setIsOpen(false);
          resetState();
          setPendingQuoteId(resolved.documentId);
          setIsChangeStatusModalOpen(true);
          return;
        }

        await postWorkTypesAndOpenQuote(
          payloads,
          resolved.documentId,
          reorderContext.clientId,
          !!resolved.isNewDocument,
          !!resolved.hasClient
        );
      } catch {
        alertFaultAdded();
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      alertFaultAdded,
      buildWorkTypesPayloads,
      hasInvalidQuantity,
      parentSku,
      postWorkTypesAndOpenQuote,
      reorderContext.clientId,
      resetState,
      resolveQuoteDocumentId,
    ]
  );

  const onReorderWithSameUnitPrice = useCallback(() => {
    submitReorder(false);
  }, [submitReorder]);

  const onReorderWithNewCalculation = useCallback(() => {
    submitReorder(true);
  }, [submitReorder]);

  const onCloseChangeStatusModal = useCallback(() => {
    setIsChangeStatusModalOpen(false);
    setPendingQuoteId(null);
    pendingWorkTypesRef.current = null;
  }, []);

  const onConfirmChangeStatus = useCallback(async () => {
    if (!pendingQuoteId) {
      return;
    }

    const pendingWorkTypes = pendingWorkTypesRef.current;
    let updateSuccess = false;

    await updateDocumentApi(
      callApi,
      (res) => {
        if (res?.success) {
          updateSuccess = true;
          return;
        }
        if (res?.data?.statusCode === 85) {
          alertFault(res?.data?.message || "customerSkus.reorderFailed");
        } else {
          alertFaultUpdate();
        }
      },
      {
        documentType: DOCUMENT_TYPE.quote,
        document: {
          documentId: pendingQuoteId,
        },
      }
    );

    if (!updateSuccess) {
      return;
    }

    onCloseChangeStatusModal();

    if (!pendingWorkTypes?.payloads?.length) {
      navigate("/quote");
      return;
    }

    let createdId: string | null = null;
    await createNewDocumentApi(
      callApi,
      (createRes) => {
        if (createRes?.success) {
          createdId = extractDocumentId(createRes);
        }
      },
      {
        documentType: DOCUMENT_TYPE.quote,
        ...(pendingWorkTypes.clientId && { clientId: pendingWorkTypes.clientId }),
      }
    );

    setIsSubmitting(true);
    try {
      await postWorkTypesAndOpenQuote(
        pendingWorkTypes.payloads,
        createdId || undefined,
        pendingWorkTypes.clientId,
        true
      );
    } catch {
      alertFaultAdded();
    } finally {
      setIsSubmitting(false);
    }
  }, [
    alertFault,
    alertFaultAdded,
    alertFaultUpdate,
    callApi,
    navigate,
    onCloseChangeStatusModal,
    pendingQuoteId,
    postWorkTypesAndOpenQuote,
  ]);

  return {
    isOpen,
    step,
    parentSku,
    relatedSkus,
    skuGroups,
    selectedRelatedIds,
    isLoadingRelated,
    isSubmitting,
    hasInvalidQuantity,
    isChangeStatusModalOpen,
    openReorderSkuModal,
    closeModal,
    onToggleRelated,
    onToggleGroup,
    onToggleAllGroups,
    onChangeQuantity,
    onReorderWithSameUnitPrice,
    onReorderWithNewCalculation,
    onCloseChangeStatusModal,
    onConfirmChangeStatus,
  };
};

export { useReorderSkuModal };
